import { forwardRef, useRef } from 'react';
import type { ChangeEvent, FocusEvent } from 'react';
import { Field, ErrorMessage, FieldProps } from 'formik';
import type { InputRef } from 'antd';
import { Input as AntInput, Flex } from 'antd';
import TextError from 'components/TextError';
import FieldLabel from 'components/FieldLabel';
import If from 'components/If';
import { InputProps } from 'utils/globalTypes';

const Input = forwardRef<InputRef, InputProps>((props, ref) => {
  const {
    label,
    name,
    qaSelector,
    inputRef,
    showErrMsg = true,
    info,
    ...rest
  } = props;
  const hasUserEditedRef = useRef(false);
  return (
    <Flex vertical align='flex-start' className='formFieldContainer'>
      <FieldLabel label={label} info={info} />
      <Field name={name}>
        {({ field, meta, form }: FieldProps) => {
          const { setFieldValue } = form;
          const fieldStatus = meta.touched && meta.error ? 'error' : '';
          const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
            hasUserEditedRef.current = true;
            (rest.onChange ?? field.onChange)(e);
          };
          const onBlur = (e: FocusEvent<HTMLInputElement>) => {
            // An auto-focused, never-edited field (see useFocus) sits above
            // same-form nav links/buttons. Marking it touched here would
            // render a "required" error and shift those controls out from
            // under the click that's still in flight, so skip validation
            // unless the user actually typed something.
            if (!hasUserEditedRef.current && e.target.value.trim() === '') {
              return;
            }
            setFieldValue(name, e.target.value.trim()).then(() => {
              field.onBlur(e);
            });
          };

          return (
            <AntInput
              status={fieldStatus}
              id={name}
              ref={ref ?? inputRef}
              {...field}
              {...rest}
              onChange={handleChange}
              onBlur={onBlur}
              data-qa-selector={qaSelector}
            />
          );
        }}
      </Field>
      <If condition={showErrMsg}>
        <ErrorMessage name={name}>
          {(msg) => <TextError msg={msg} qaSelector={`${qaSelector}-error`} />}
        </ErrorMessage>
      </If>
    </Flex>
  );
});

export default Input;
